import { useEffect, useState, useRef } from "react";
import { Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Rss, Kanban, Users, User, Bell, LogOut, ShoppingBag, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { api, leaveGuestToRegister } from "@/lib/api";
import Logo from "@/components/Logo";
import PushBanner from "@/components/PushBanner";

const TABS = [
  { id: "feed", key: "nav_feed", icon: Rss, path: "/feed" },
  { id: "marketplace", key: "nav_marketplace", icon: ShoppingBag, path: "/marketplace" },
  { id: "pipeline", key: "nav_pipeline", icon: Kanban, path: "/pipeline" },
  { id: "network", key: "nav_network", icon: Users, path: "/network" },
  { id: "profilo", key: "nav_profilo", icon: User, path: "/profilo" },
];

export default function AppShell() {
  const { user, logout, isGuest } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const seenRef = useRef(new Set(JSON.parse(localStorage.getItem("enb_seen_notif") || "[]")));
  const tabs = isGuest ? TABS.filter((tb) => ["feed", "marketplace", "network"].includes(tb.id)) : TABS;

  useEffect(() => {
    if (user?.lang && user.lang !== lang) setLang(user.lang);
  }, [user, lang, setLang]);

  useEffect(() => {
    try {
      if ("Notification" in window && Notification.permission === "default") {
        const r = Notification.requestPermission();
        if (r && typeof r.then === "function") r.catch(() => {});
      }
    } catch {}
  }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications/unread-count");
        if (active) setUnread(data.count);
        const { data: notifs } = await api.get("/notifications");
        const firstLoad = seenRef.current.size === 0;
        notifs.forEach((n) => {
          if (!seenRef.current.has(n.id)) {
            if (!firstLoad && !n.read) {
              toast.success(n.title, { description: n.body, duration: 6000 });
              if ("Notification" in window && Notification.permission === "granted") {
                try { new Notification("ENBISSY · " + n.title, { body: n.body }); } catch {}
              }
            }
            seenRef.current.add(n.id);
          }
        });
        localStorage.setItem("enb_seen_notif", JSON.stringify([...seenRef.current].slice(-200)));
      } catch {}
    };
    load();
    const iv = setInterval(load, 15000);
    return () => { active = false; clearInterval(iv); };
  }, [location.pathname]);

  const isActive = (p) => location.pathname === p || location.pathname.startsWith(p + "/");
  if (isGuest && !["/feed", "/marketplace", "/network"].some((p) => isActive(p))) return <Navigate to="/feed" replace />;

  return (
    <div className="min-h-screen bg-neutral-100">
      <div className="mx-auto w-full max-w-md md:max-w-6xl min-h-screen bg-white md:bg-transparent md:grid md:grid-cols-12 md:gap-6 md:px-6 md:py-6">
        {/* Desktop left rail */}
        <aside className="hidden md:flex md:col-span-3 flex-col gap-1 sticky top-6 self-start">
          <div className="mb-6 px-2"><Logo size={24} /></div>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = isActive(tab.path);
            return (
              <button
                key={tab.id}
                data-testid={`nav-${tab.id}`}
                onClick={() => navigate(tab.path)}
                className={`flex items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors ${active ? "bg-black text-white" : "text-neutral-700 hover:bg-neutral-100"}`}
              >
                <Icon size={20} strokeWidth={active ? 2.2 : 1.8} />
                <span className="font-medium">{t(tab.key)}</span>
              </button>
            );
          })}
          {!isGuest && <button
            data-testid="nav-notifiche"
            onClick={() => navigate("/notifiche")}
            className={`flex items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors ${isActive("/notifiche") ? "bg-black text-white" : "text-neutral-700 hover:bg-neutral-100"}`}
          >
            <Bell size={20} />
            <span className="font-medium">{t("nav_notifiche")}</span>
            {unread > 0 && <span className="ml-auto bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5">{unread}</span>}
          </button>}
          <button data-testid="logout-btn" onClick={logout} className="mt-4 flex items-center gap-3 rounded-xl px-4 py-3 text-left text-neutral-500 hover:bg-neutral-100 transition-colors">
            <LogOut size={20} /><span className="font-medium">{t("logout")}</span>
          </button>
        </aside>

        {/* Main column */}
        <main className="md:col-span-9 lg:col-span-9 w-full pb-24 md:pb-6">
          {/* Mobile top bar */}
          <header className="md:hidden sticky top-0 z-40 flex items-center justify-between bg-white/95 backdrop-blur-md border-b border-neutral-200 px-4 h-14">
            <Logo size={20} />
            {isGuest ? (
              <button data-testid="topbar-guest-register" onClick={leaveGuestToRegister} className="h-8 px-3 rounded-full bg-black text-white text-xs font-semibold">{t("guest_register")}</button>
            ) : (
              <button data-testid="topbar-notifiche" onClick={() => navigate("/notifiche")} className="relative p-2 -mr-2">
                <Bell size={22} />
                {unread > 0 && <span className="absolute top-0 right-0 bg-red-500 text-white text-[9px] rounded-full px-1">{unread}</span>}
              </button>
            )}
          </header>

          <div className="md:max-w-2xl md:mx-auto">
            {isGuest && (
              <div data-testid="guest-banner" className="m-3 md:m-0 md:mb-3 rounded-2xl bg-[#E0B94B]/15 border border-[#E0B94B]/40 px-4 py-3 flex items-center gap-3">
                <Eye size={18} className="shrink-0" />
                <p className="text-sm flex-1">{t("guest_banner")}</p>
                <button data-testid="guest-register-btn" onClick={leaveGuestToRegister} className="h-9 px-4 rounded-full bg-black text-white text-sm font-semibold shrink-0">{t("guest_register")}</button>
              </div>
            )}
            {!isGuest && <PushBanner />}
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-t border-neutral-200 flex justify-around px-1 py-1.5">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = isActive(tab.path);
          return (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => navigate(tab.path)}
              className="flex flex-col items-center justify-center gap-0.5 min-w-[56px] min-h-[48px] rounded-lg transition-colors"
            >
              <Icon size={22} strokeWidth={active ? 2.4 : 1.8} className={active ? "text-black" : "text-neutral-400"} />
              <span className={`text-[10px] ${active ? "text-black font-semibold" : "text-neutral-400"}`}>{t(tab.key)}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
